import Image from 'next/image';
import Link from 'next/link';
import { awsImage } from '@firsttable/functions';
import type { FilteredRestaurant } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import AwardIcon from '@discover-ui/icons/award-02.svg';
import Skeleton from '@discover-ui/skeleton';
import Stars from '@discover-components/restaurant/rating/stars';

type HomepageRestaurant = Pick<
  FilteredRestaurant,
  | 'menuTitle'
  | 'slug'
  | 'gallery'
  | 'rating'
  | 'approvedReviewsCount'
  | 'suburb'
  | 'region'
  | 'cuisines'
  | 'regionRanking'
>;

export default function PD6630RestaurantCard({
  restaurant,
  imagePriority = false,
}: {
  restaurant: HomepageRestaurant;
  imagePriority?: boolean;
}) {
  const location = restaurant.suburb?.menuTitle || restaurant.region?.menuTitle;
  const cuisine = restaurant.cuisines?.edges?.[0]?.node?.title;
  const metadata = [location, cuisine].filter(Boolean).join(' • ');
  const hasRating = !!restaurant.rating && !!restaurant.approvedReviewsCount;
  const topRated =
    restaurant.regionRanking != null && restaurant.regionRanking > 0
      ? `Top rated #${restaurant.regionRanking}`
      : null;

  return (
    <article className="flex h-[260px] w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all active:scale-[0.98] active:opacity-80">
      <Link
        href={restaurant.slug || '#'}
        draggable={false}
        className="relative block overflow-hidden no-underline">
        <Image
          src={
            restaurant.gallery
              ? awsImage(restaurant.gallery, 420, 420)
              : '/images/restaurant-placeholder.jpg'
          }
          alt={`Photo of ${restaurant.menuTitle || 'a restaurant'}`}
          width={300}
          height={300}
          priority={imagePriority}
          draggable={false}
          className="h-[150px] w-full object-cover"
        />
        {topRated ? (
          <span className="absolute left-2 top-2 z-10 inline-flex h-6 items-center gap-0.5 rounded-full bg-ds-gold-100 py-0.5 pl-1.5 pr-2 text-2xs font-semibold text-ds-gold-800">
            <AwardIcon aria-hidden="true" className="size-4 shrink-0 text-ds-gold-700" />
            {topRated}
          </span>
        ) : null}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-4 pb-5 pt-4">
        <h3 className="m-0 truncate text-base font-semibold text-black">
          <Link
            href={restaurant.slug || '#'}
            draggable={false}
            className="text-current no-underline hover:underline">
            {restaurant.menuTitle}
          </Link>
        </h3>
        <p className="truncate text-xs font-normal text-gray">{metadata}</p>
        <div className="flex h-5 items-center">
          {hasRating && (
            <Link
              href={restaurant.slug ? `${restaurant.slug}#reviews` : '#'}
              draggable={false}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 no-underline">
              <Stars count={restaurant.rating} size={20} gap={0} />
              <span>
                {restaurant.rating.toFixed(1)} ({restaurant.approvedReviewsCount})
              </span>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export function PD6630RestaurantCardSkeleton() {
  return (
    <article className="flex h-[260px] w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <Skeleton className="h-[150px] w-full rounded-none" />
      <div className="flex flex-1 flex-col gap-1.5 px-4 pb-5 pt-4">
        <Skeleton className="h-[22px] w-3/4 rounded" />
        <Skeleton className="h-5 w-1/2 rounded" />
        <Skeleton className="h-5 w-2/5 rounded" />
      </div>
    </article>
  );
}
